/**
 * FAQ Page
 * Frequently asked questions about CalWMP and CalGreen requirements
 */

import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string | JSX.Element;
  category: 'general' | 'technical' | 'compliance' | 'platform';
}

const faqs: FAQItem[] = [
  {
    category: 'general',
    question: 'What is CalWMP?',
    answer: 'CalWMP (California Waste Management Plans) is a free online tool that helps construction professionals create waste management plans that comply with CalGreen Section 5.408 requirements. We simplify the process of documenting your construction waste diversion strategy.',
  },
  {
    category: 'general',
    question: 'Do I need to create an account to use CalWMP?',
    answer: (
      <span>
        No! You can start creating a plan immediately without logging in. When you&apos;re ready to save your progress,
        you&apos;ll simply provide your email address to receive a shareable link. This link lets you return to your plan
        at any time to continue editing or submit it.
      </span>
    ),
  },
  {
    category: 'general',
    question: 'Is CalWMP affiliated with the State of California?',
    answer: 'No. CalWMP is an independent service provider and is not affiliated with, endorsed by, or connected to the State of California or any government agency. We provide this tool as a convenience for construction professionals working to comply with California regulations.',
  },
  {
    category: 'compliance',
    question: 'What is the minimum waste diversion rate required?',
    answer: 'CalGreen Section 5.408 requires projects to divert at least 65% of construction and demolition debris from landfills by weight. This means recycling, reusing, salvaging, or otherwise diverting at least 65% of your waste materials.',
  },
  {
    category: 'compliance',
    question: 'Which projects need to comply with CalGreen 5.408?',
    answer: 'Most non-residential construction, renovation, and demolition projects must comply. Many jurisdictions also require compliance for residential projects over a certain size (often 1,000+ square feet). Requirements vary by local jurisdiction, so always check with your local building department.',
  },
  {
    category: 'compliance',
    question: 'Can I count on-site reuse toward my diversion rate?',
    answer: 'Yes! Materials reused on-site (like crushing concrete for base material or using excavated soil for landscaping) can count toward your diversion rate. Be sure to document the quantity and method of reuse.',
  },
  {
    category: 'compliance',
    question: 'What happens if I don&apos;t meet the 65% requirement?',
    answer: 'Failing to meet the requirement can result in delayed permits, withheld certificates of occupancy, fines, or required corrective action plans. It&apos;s important to plan ahead and track your diversion carefully throughout the project.',
  },
  {
    category: 'technical',
    question: 'How do I estimate waste quantities?',
    answer: 'Base estimates on project square footage, similar past projects, or industry standards. For demolition, you can estimate based on building size and construction type. Our system provides guidance, but consult with your hauler or recycling facilities for more accurate estimates.',
  },
  {
    category: 'technical',
    question: 'Should I report waste in tons or cubic yards?',
    answer: 'CalGreen requires diversion rates to be calculated by weight (tons). While some haulers measure in cubic yards, you\'ll need weight tickets from facilities to prove compliance. Most recycling facilities provide weight-based documentation.',
  },
  {
    category: 'technical',
    question: 'What materials are easiest to recycle?',
    answer: 'Metal, concrete, asphalt, and clean wood are typically the easiest and most valuable materials to recycle. They often have established recycling markets and facilities. Mixed materials and contaminated waste are more difficult to divert.',
  },
  {
    category: 'technical',
    question: 'How do I find recycling facilities near my project?',
    answer: (
      <span>
        Search online for &quot;C&amp;D recycling&quot; or specific materials (like &quot;concrete recycling&quot;) in your area.
        CalRecycle maintains a{' '}
        <a
          href="https://www2.calrecycle.ca.gov/SWFacilities/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 underline"
        >
          database of facilities
        </a>
        . You can also ask your waste hauler for recommendations.
      </span>
    ),
  },
  {
    category: 'platform',
    question: 'How is my data stored and protected?',
    answer: 'Your plan data is stored securely. For guest users, data is initially saved in your browser\'s local storage and then uploaded to our servers when you request a shareable link. For authenticated users, all data is stored with encryption. We never share your data with third parties.',
  },
  {
    category: 'platform',
    question: 'How long does my shareable link remain active?',
    answer: 'Shareable links remain active for 30 days from creation. This gives you plenty of time to complete and submit your plan. If you need longer access, you can create a free account to keep your plans indefinitely.',
  },
  {
    category: 'platform',
    question: 'Can multiple people work on the same plan?',
    answer: 'With a shareable link, anyone can view the plan, but only the owner (with the link) can make edits. If you need true collaborative editing, consider creating an account and sharing the link with your team members.',
  },
  {
    category: 'platform',
    question: 'Can I export or print my plan?',
    answer: 'Yes! Once your plan is complete, you can download it as a PDF document suitable for submission to your local jurisdiction. The PDF includes all project details, waste streams, and compliance calculations.',
  },
  {
    category: 'compliance',
    question: 'Do I need to submit my plan before starting work?',
    answer: 'Requirements vary by jurisdiction. Many jurisdictions require the waste management plan to be submitted with your building permit application, while others want it before the final inspection. Check with your local building department for specific requirements.',
  },
  {
    category: 'compliance',
    question: 'What documentation do I need to keep?',
    answer: 'Keep all weight tickets, receipts, and documentation from recycling facilities and haulers. You\'ll need these to prove your final diversion rate. Take photos of materials being separated and hauled away as additional evidence.',
  },
  {
    category: 'technical',
    question: 'Can I update my plan after submission?',
    answer: 'Once submitted to the jurisdiction, you typically cannot make changes unless the jurisdiction requests revisions. However, you can create updated versions of your plan in CalWMP and download new PDFs as needed.',
  },
  {
    category: 'platform',
    question: 'Is CalWMP really free?',
    answer: 'Yes, CalWMP is completely free to use. We believe that compliance tools should be accessible to all construction professionals, regardless of project size. There are no hidden fees or premium features.',
  },
  {
    category: 'general',
    question: 'What if my jurisdiction has additional requirements?',
    answer: 'Some jurisdictions have requirements beyond CalGreen 5.408, such as higher diversion rates, specific forms, or additional documentation. Always check with your local building department and use CalWMP as one part of your compliance strategy.',
  },
  {
    category: 'platform',
    question: 'Can I get help if I\'m stuck?',
    answer: (
      <span>
        Yes! Throughout the platform, you&apos;ll find help tooltips and examples. You can also email us at{' '}
        <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">
          support@calwmp.com
        </a>{' '}
        for assistance. We&apos;re here to help you succeed.
      </span>
    ),
  },
];

function FAQAccordion({ faq, isOpen, onClick }: { faq: FAQItem; isOpen: boolean; onClick: () => void }) {
  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={onClick}
        className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-neutral-50 transition-colors text-left"
      >
        <span className="font-medium text-neutral-900">{faq.question}</span>
        <svg
          className={`w-5 h-5 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200">
          <div className="text-neutral-700 leading-relaxed">{faq.answer}</div>
        </div>
      )}
    </div>
  );
}

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { value: 'all', label: 'All Questions' },
    { value: 'general', label: 'General' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'technical', label: 'Technical' },
    { value: 'platform', label: 'Platform' },
  ];

  const filteredFaqs =
    selectedCategory === 'all' ? faqs : faqs.filter((faq) => faq.category === selectedCategory);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          ← Back to Home
        </Link>
      </div>

      <h1 className="text-4xl font-bold text-neutral-900 mb-4">Frequently Asked Questions</h1>

      <p className="text-lg text-neutral-600 mb-8">
        Common questions about CalWMP and CalGreen 5.408 compliance
      </p>

      {/* Category Filter */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => {
                setSelectedCategory(category.value);
                setOpenIndex(null);
              }}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                selectedCategory === category.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* FAQ List */}
      <div className="space-y-3 mb-12">
        {filteredFaqs.map((faq, index) => (
          <FAQAccordion
            key={index}
            faq={faq}
            isOpen={openIndex === index}
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
          />
        ))}
      </div>

      {/* Still Have Questions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Still have questions?</h3>
        <p className="text-blue-800 mb-4">
          Can&apos;t find what you&apos;re looking for? We&apos;re here to help!
        </p>
        <a
          href="mailto:support@calwmp.com"
          className="inline-block px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          Contact Support
        </a>
      </div>

      <div className="mt-12 pt-6 border-t border-neutral-200">
        <p className="text-sm text-neutral-600">
          <strong>Disclaimer:</strong> This FAQ is provided for informational purposes only and does not constitute
          legal or regulatory advice. Requirements may vary by jurisdiction. Always consult with your local building
          department for specific compliance requirements.
        </p>
      </div>
    </div>
  );
}